import React, { useState, useMemo, useEffect } from "react";
import { Modal, Stack, Select, Button, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { SelectedService } from "../../../../../../types/availability";
import { useSalon } from "../../../../../../context/SalonContext";
import { useCalendar } from "../../../../../../context/CalendarContext";
import { useResponsive } from "../../../../../../context/ResponsiveContext";
import { SalonService, Employee } from "../../../../../../types/salon";
import { StaffServiceMapping } from "../../../../../../types/calendar";
import { filterBookableActiveStaff } from "../../../../../../utils/staffUtils";
import styles from "./RebookModal.module.css";

interface AddServiceModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: (service: SelectedService) => void;
  existingServiceIds?: string[]; // To prevent duplicates
}

export default function AddServiceModal({
  opened,
  onClose,
  onSuccess,
  existingServiceIds = [],
}: AddServiceModalProps) {
  const { isMobile } = useResponsive();
  const { employees, services } = useSalon() as {
    employees: Employee[] | undefined;
    services: SalonService[] | undefined;
  };
  const { staffServices } = useCalendar() as {
    staffServices: StaffServiceMapping[] | undefined;
  };

  // Form state
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
    null,
  );
  const [selectedSalonServiceId, setSelectedSalonServiceId] = useState<
    string | null
  >(null);

  // Filtered options state (for bidirectional filtering)
  const [availableStaff, setAvailableStaff] = useState<Employee[]>(() =>
    filterBookableActiveStaff(employees || [], selectedStaffId || ""),
  );
  const [availableServices, setAvailableServices] = useState<SalonService[]>(
    services || [],
  );

  // Update available staff when employees load or change
  useEffect(() => {
    if (!selectedSalonServiceId) {
      setAvailableStaff(
        filterBookableActiveStaff(employees || [], selectedStaffId || ""),
      );
    }
  }, [employees, selectedStaffId, selectedSalonServiceId]);

  // Filter services when staff is selected
  useEffect(() => {
    if (selectedStaffId && staffServices && services) {
      const staffServiceIds = staffServices
        .filter((ss) => ss.staffId === selectedStaffId)
        .map((ss) => ss.salonServiceId);

      const filteredServices = services.filter((service) =>
        staffServiceIds.includes(service.id),
      );

      setAvailableServices(filteredServices);
    } else if (services) {
      setAvailableServices(services);
    }
  }, [selectedStaffId, staffServices, services]);

  // Filter staff when service is selected
  useEffect(() => {
    if (selectedSalonServiceId && services && employees) {
      const selectedService = services.find(
        (s) => s.id === selectedSalonServiceId,
      );

      if (selectedService) {
        const availableStaffIds =
          selectedService.servicing_staff?.map((staff) => staff.staff_id) || [];
        const filteredStaff = filterBookableActiveStaff(
          employees,
          selectedStaffId || "",
        ).filter((emp) => availableStaffIds.includes(emp.id));

        setAvailableStaff(filteredStaff);
      }
    } else {
      setAvailableStaff(
        filterBookableActiveStaff(employees || [], selectedStaffId || ""),
      );
    }
  }, [selectedSalonServiceId, services, employees, selectedStaffId]);

  // Get staff options
  const staffOptions = useMemo(() => {
    return availableStaff.map((emp) => ({
      value: emp.id,
      label: `${emp.first_name} ${emp.last_name}`,
    }));
  }, [availableStaff]);

  // Get service options based on available services and selected staff
  const serviceOptions = useMemo(() => {
    if (!staffServices) return [];

    return availableServices.map((service) => {
      // Find staff-specific pricing and duration from servicing_staff
      const staffService = selectedStaffId
        ? service.servicing_staff?.find((ss) => ss.staff_id === selectedStaffId)
        : null;
      // Get the staff_service_id from staffServices mapping
      const staffServiceMapping = selectedStaffId
        ? staffServices.find(
            (ss) =>
              ss.staffId === selectedStaffId &&
              ss.salonServiceId === service.id,
          )
        : staffServices.find((ss) => ss.salonServiceId === service.id);

      return {
        value: staffServiceMapping?.id || service.id,
        label: service.name,
        staffServiceId: staffServiceMapping?.id || "",
        salonServiceId: service.id,
        price: staffService?.price ?? service.default_price ?? 0,
        baseDuration:
          staffService?.base_duration_mins ??
          service.default_base_duration_mins ??
          30,
        processingTime:
          staffService?.processing_time_mins ??
          service.default_processing_time_mins ??
          0,
        finishingTime:
          staffService?.finishing_time_mins ??
          service.default_finishing_time_mins ??
          0,
        bufferTime:
          staffService?.buffer_time_mins ??
          service.default_buffer_time_mins ??
          0,
      };
    });
  }, [availableServices, selectedStaffId, staffServices]);

  // Handle staff selection change
  const handleStaffChange = (value: string | null) => {
    if (value && staffServices && services) {
      const staffServiceIds = staffServices
        .filter((service) => service.staffId === value)
        .map((service) => service.salonServiceId);

      const filteredServices = services.filter((service) =>
        staffServiceIds.includes(service.id),
      );

      setAvailableServices(filteredServices);
    } else if (services) {
      setAvailableServices(services);
    }

    setSelectedStaffId(value);
    setSelectedServiceId(null);
    setSelectedSalonServiceId(null);
  };

  // Handle service selection change
  const handleServiceChange = (value: string | null) => {
    const selectedOption = serviceOptions.find((opt) => opt.value === value);
    const salonServiceId = selectedOption?.salonServiceId || null;

    if (salonServiceId && services && employees) {
      const selectedService = services.find((s) => s.id === salonServiceId);

      if (selectedService) {
        const availableStaffIds =
          selectedService.servicing_staff?.map((staff) => staff.staff_id) || [];
        const filteredStaff = filterBookableActiveStaff(
          employees,
          selectedStaffId || "",
        ).filter((emp) => availableStaffIds.includes(emp.id));

        setAvailableStaff(filteredStaff);
      }
    } else if (employees) {
      setAvailableStaff(
        filterBookableActiveStaff(employees, selectedStaffId || ""),
      );
    }

    setSelectedServiceId(value);
    setSelectedSalonServiceId(salonServiceId);
  };

  // Handle adding the service
  const handleAddService = () => {
    if (!selectedStaffId || !selectedServiceId) return;

    const selectedStaff = employees?.find((emp) => emp.id === selectedStaffId);
    const selectedServiceOption = serviceOptions.find(
      (s) => s.value === selectedServiceId,
    );

    if (!selectedStaff || !selectedServiceOption) return;

    const newService: SelectedService = {
      id: `new-${Date.now()}`, // Unique ID for new services
      staffServiceId: selectedServiceOption.staffServiceId,
      salonServiceId: selectedServiceOption.salonServiceId,
      staffId: selectedStaffId,
      staffName: `${selectedStaff.first_name} ${selectedStaff.last_name}`,
      serviceName: selectedServiceOption.label,
      price: selectedServiceOption.price,
      totalDurationDelta: 0,
      baseDuration: selectedServiceOption.baseDuration,
      processingTime: selectedServiceOption.processingTime,
      finishingTime: selectedServiceOption.finishingTime,
      bufferTime: selectedServiceOption.bufferTime,
      isNew: true,
    };

    onSuccess(newService);
    handleClose();
  };

  // Handle closing the modal
  const handleClose = () => {
    setSelectedStaffId(null);
    setSelectedServiceId(null);
    setSelectedSalonServiceId(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add Service"
      size="md"
      fullScreen={isMobile}
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Select a staff member and service to add to this appointment.
        </Text>

        <Select
          label="Staff Member"
          placeholder="Select staff..."
          data={staffOptions}
          value={selectedStaffId}
          onChange={handleStaffChange}
          searchable
          clearable
          required
        />

        <Select
          label="Service"
          placeholder="Select a service..."
          data={serviceOptions}
          value={selectedServiceId}
          onChange={handleServiceChange}
          searchable
          clearable
          required
          disabled={!selectedStaffId && availableServices.length > 10} // Only disable if no staff and many services
        />

        <Button
          color="teal"
          fullWidth
          leftSection={<IconPlus size={16} />}
          onClick={handleAddService}
          disabled={!selectedStaffId || !selectedServiceId}
        >
          Add Service
        </Button>
      </Stack>
    </Modal>
  );
}
